import React, { useState, useEffect } from "react";
import Seo from "../../../shared/ui/Seo";
import { useParams } from "react-router-dom";
import { getProductsByAccountId } from "../api";
import LoadMoreButton from "../../../shared/ui/LoadMoreButton";
import { mergeProducts } from "../../products/utils/productDisplay";
import {
  S3_URL_VENDOR,
  DEFAULT_VENDOR_CODE,
  DEFAULT_VENDOR_IMAGE,
  DEFAULT_VENDOR_COVER_IMAGE,
} from "../../../shared/utils/constants";
import ProductGrid from "../../products/components/ProductGrid";
import Loading from "../../../shared/ui/Loading";
import NoResults from "../../../shared/ui/NoResults";
const VendorProducts = () => {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vendorCode, setVendorCode] = useState("");
  const [vendorLogo, setVendorLogo] = useState("");
  const [vendorCoverImage, setVendorCoverImage] = useState("");
  const pageSize = 20;

  const [sort, setSort] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchProducts = async (page) => {
    setLoading(true);
    try {
      const response = await getProductsByAccountId(
        id,
        page,
        pageSize,
        sort,
        search
      );
      setProducts((prev) => mergeProducts(prev, response.products, page));

      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      setVendorCode(
        response.vendorCode ? response.vendorCode : DEFAULT_VENDOR_CODE
      );

      setVendorLogo(
        response.vendorLogo
          ? `${S3_URL_VENDOR}${response.vendorLogo}`
          : DEFAULT_VENDOR_IMAGE
      );
      setVendorCoverImage(
        response.vendorCoverImage
          ? `${S3_URL_VENDOR}${response.vendorCoverImage}`
          : DEFAULT_VENDOR_COVER_IMAGE
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, sort, search]);

  const handleSortChange = (e) => {
    setSort(e.target.value);
    setCurrentPage(0); // Reset pagination on sort change
  };

  if (products.length === 0 && !loading) {
    return <NoResults message="No products found." />;
  }

  return (
    <section className="vendor-two-details">
      <Seo
        title={`Vendor Page ${vendorCode}`}
        description={`Shop products from ${vendorCode} on Bigmall, Sri Lanka's online shopping mall. Browse the full range with secure payments and island-wide delivery.`}
        image={vendorLogo?.startsWith("http") ? vendorLogo : undefined}
      />
      <div className="container container-lg">
        <div className="vendor-header mt-4">
          {/* Cover Image as a background */}
          <div
            className="cover-image bg-img"
            style={{
              backgroundImage: `url(${vendorCoverImage})`,
            }}
          >
            {/* Overlay with vendor details */}
            <div className="vendor-overlay d-flex justify-content-between align-items-center px-16 py-8">
              {/* Vendor Info (Logo, name, followers, rating) */}
              <div className="vendor-info d-flex align-items-center gap-16">
                <div
                  className="vendor-logo flex-shrink-0"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={vendorLogo}
                    alt="Vendor Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
                <div className="vendor-meta">
                  <h6 className="text-white fw-semibold mb-4">{vendorCode}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && products.length === 0 ? (
          <Loading message="Loading products…" />
        )  : (
          <>
            <div className="vendor-two-details__contents">
              <div className="d-flex align-items-center justify-content-between flex-wrap mt-40 mb-32 gap-16">
                <div className="container">
                  <div className="row align-items-center g-3">
                    <div className="col-12 col-md-4">
                      <input
                        type="text"
                        placeholder="Search by name"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="form-control"
                      />
                    </div>
                    <div className="col-6 col-md-2">
                      <button
                        onClick={() => {
                          setSearch(searchInput);
                          setCurrentPage(0);
                        }}
                        disabled={searchInput.length < 3}
                        className="btn btn-danger btn-sm w-100"
                      >
                        Search
                      </button>
                    </div>
                    <div className="col-12 col-md-4">
                      <select
                        value={sort}
                        onChange={handleSortChange}
                        className="form-select"
                      >
                        <option value="">Sort By</option>
                        <option value="lowest">
                          Lowest Price to Highest Price
                        </option>
                        <option value="highest">
                          Highest Price to Lowest Price
                        </option>
                        <option value="new">New Arrivals</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-2">
                      <button
                        onClick={() => {
                          setSearch("");
                          setSearchInput("");
                          setSort("");
                          setCurrentPage(0);
                        }}
                        className="btn btn-danger btn-sm w-100"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between justify-content-sm-end gap-16 flex-grow-1">
                  <span className="text-gray-900">
                    Showing {currentPage * pageSize + 1}-
                    {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
                    {totalElements} results
                  </span>
                </div>
              </div>
              <ProductGrid
                products={products}
                currentPage={currentPage}
                totalPages={totalPages}
                showLoadmore={false}
                margin_top="product"
              />
            </div>
            <div className="container">
              <LoadMoreButton
          hasMore={currentPage < totalPages - 1}
          loading={loading}
          onClick={() => setCurrentPage((page) => page + 1)}
        />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default VendorProducts;
